import { useState, useEffect, useRef, KeyboardEvent as ReactKeyboardEvent, forwardRef, useImperativeHandle } from 'react';
import { Stack, Text, Group, Box, UnstyledButton } from '@mantine/core';
import classes from './AgentsModal.module.css';
import { IconCheck } from '@tabler/icons-react';
import { useChatStore } from '../../../../stores/chatStore';
import { useAgents } from '../../../../api/agents';
import { Agent } from '../../../../api/base/types';
import { GENIE_SPACE_AGENT_IDS } from '../../../../constants/agents';

export interface AtAgentsModalRef {
  handleKeyDown: (
    e: ReactKeyboardEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLTextAreaElement>
  ) => boolean;
}

interface AgentsModalProps {
  onAgentSelect?: (agent: Agent) => void;
  onAgentRemove?: (agentId: string) => void;
  selectedAgents?: string[];
  mentionFilter?: string;
  removeAtInput?: () => void;
}

export const AtAgentsModal = forwardRef<AtAgentsModalRef, AgentsModalProps>(
  ({ onAgentSelect, onAgentRemove, selectedAgents = [], mentionFilter, removeAtInput }, ref) => {
    const [focusedIndex, setFocusedIndex] = useState(0);
    const modalRef = useRef<HTMLDivElement>(null);
    const genieSpaceAgentIds = new Set<string>(GENIE_SPACE_AGENT_IDS);

    // Get Genie access state from chat store
    const allowedGenieSpaceNames = useChatStore(state => state.allowedGenieSpaceNames);

    // Fetch agents from API
    const { data: agents = [], isLoading } = useAgents();

    const handleAgentClick = (agent: Agent) => {
      if (selectedAgents.includes(agent.id)) {
        onAgentRemove?.(agent.id);

        removeAtInput?.();
      } else {
        onAgentSelect?.(agent);
        removeAtInput?.();

      }
    };

    // Filter agents based on mentionFilter and Genie space permissions
    const filteredAgents = (mentionFilter
      ? agents.filter(agent =>
          agent.display_name.toLowerCase().includes(mentionFilter.toLowerCase()) ||
          agent.name.toLowerCase().includes(mentionFilter.toLowerCase())
        )
      : agents
    ).filter(agent => {
      if (genieSpaceAgentIds.has(agent.id)) {
        return allowedGenieSpaceNames.includes(agent.id);
      }
      return true;
    });

    useEffect(() => {
      setFocusedIndex(0);
    }, [mentionFilter]);

    // Handle keyboard navigation
    const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement> | ReactKeyboardEvent<HTMLTextAreaElement>) => {
      if (filteredAgents.length === 0) {
        return false;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        return true;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => (prev < filteredAgents.length - 1 ? prev + 1 : prev));
        return true;
      }

      if (e.key === 'Enter' && filteredAgents[focusedIndex]) {
        e.preventDefault();
        handleAgentClick(filteredAgents[focusedIndex]);
        return true;
      }

      return false;
    };

    // Expose handleKeyDown via ref so parent can call handleKeyDown
    useImperativeHandle(ref, () => ({
      handleKeyDown
    }));

    return (
      <Box
        className={`${classes.modal} ${classes.atAgentsModal}`}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        ref={modalRef}
      >
        <Stack gap={0}>
          <Box className={classes.sectionHeader}>
            <Text className={classes.sectionTitle}>Select Agent</Text>
          </Box>

          {isLoading ? (
            <Box p="md">
              <Text size="sm" c="dimmed">Loading agents...</Text>
            </Box>
          ) : (
            <Stack gap={0}>
              {filteredAgents.map((agent, index) => {
                const isSelected = selectedAgents.includes(agent.id);
                const isFocused = index === focusedIndex;
                return (
                  <UnstyledButton
                    key={agent.id}
                    className={`${classes.agentItem} ${isSelected ? classes.selected : ''} ${isFocused ? classes.focused : ''}`}
                    onClick={() => handleAgentClick(agent)}
                  >
                    <Group gap={9} align="flex-start">
                      <Box className={classes.agentContent}>
                        <Text className={classes.agentName}>{agent.display_name}</Text>
                        <Text className={classes.agentDescription}>{agent.description}</Text>
                      </Box>
                      {isSelected && (
                        <Box className={classes.checkIcon}>
                          <IconCheck size={20} color="#59B836" />
                        </Box>
                      )}
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
          )}
        </Stack>
      </Box>
    );
  }
);

export type { AgentsModalProps };
